import moment from "moment";
import { formatAmount, normalizeDecimal, toDateString } from "views/app/FundValuationReportEdit/fundValuationPayload";

// Fair values next to the portfolio summary's market values (server docs/fund-fair-value.md).
// Pure logic, kept out of PortfolioSummary.js so the rules are unit-tested without rendering.

export const FAIR_VALUE_VIEWS = ["ifrs", "live"];
export const FAIR_VALUE_SOURCES = ["report", "market_price", "acquisition_cost"];

// An empty cell is this string, never { content: "" } (TableAuto renders the object itself
// then, React error #31).
export const EMPTY_CELL = "–";

// Decimal string or number -> number, empty or not a number -> null.
export const toNumber = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const n = normalizeDecimal(value);
  return n === null ? null : Number(n);
};

// Money is summed in cents so 0.1 + 0.2 stays 0.30.
const toCents = (value) => Math.round(value * 100);

// date_transaction may be a "YYYY-MM-DD" string, a Date or a moment: the local calendar day.
const toDay = (value) => {
  if (moment.isMoment(value)) {
    return value.isValid() ? value.format("YYYY-MM-DD") : null;
  }
  return toDateString(value);
};

// "2026-06-30" -> "30.06.2026", missing -> "".
export const formatDay = (value) => {
  const day = toDateString(value);
  if (!day) {
    return "";
  }
  const [y, m, d] = day.split("-");
  return `${d}.${m}.${y}`;
};

// The fair value request from the summary request's params. The endpoint has no sector,
// tag or type filter: those apply because the values are merged onto the visible summary
// rows. The params object is not changed.
export const toFairValueParams = (params) => {
  const p = params || {};
  const out = { date: toDay(p.date_transaction) || toDateString(new Date()) };
  if (p.portfolio !== null && p.portfolio !== undefined) {
    out.portfolio = Array.isArray(p.portfolio) ? [...p.portfolio] : p.portfolio;
  }
  if (p.selected_currency) {
    out.selected_currency = p.selected_currency;
  }
  if (p.currency_date_logic) {
    out.currency_date_logic = p.currency_date_logic;
  }
  return out;
};

export const fairValueKey = (type, id) => `${String(type)}:${id}`;

export const normalizeReport = (report) => {
  if (!report || typeof report !== "object") {
    return null;
  }
  return {
    id: report.id ?? null,
    value_date: toDateString(report.value_date),
    report_period: report.report_period ? String(report.report_period) : "",
    report_received: toDateString(report.report_received),
    ifrs_date: toDateString(report.ifrs_date),
    received_used: toDateString(report.received_used),
    ifrs_date_used: toDateString(report.ifrs_date_used),
    market_value_converted: toNumber(report.market_value_converted),
    transactions_net: toNumber(report.transactions_net),
    attachment_name: report.attachment_name || null,
  };
};

export const normalizeFairValueRow = (row, portfolio = {}) => {
  const r = row || {};
  const type = r.related_model_type === null || r.related_model_type === undefined ? "" : String(r.related_model_type);
  const id = r.related_model_id ?? null;
  return {
    key: fairValueKey(type, id),
    related_model_type: type,
    related_model_id: id,
    name: r.name ? String(r.name) : "",
    portfolio: portfolio?.portfolio ?? r.portfolio ?? null,
    portfolio_name: portfolio?.portfolio_name || r.portfolio_name || "",
    market_value: toNumber(r.market_value),
    fair_value: toNumber(r.fair_value),
    difference: toNumber(r.difference),
    source: FAIR_VALUE_SOURCES.includes(r.source) ? r.source : null,
    report: normalizeReport(r.report),
  };
};

// The nested list response -> one flat row per portfolio and security.
export const normalizeFairValuesResponse = (data) => {
  const portfolios = Array.isArray(data?.portfolios) ? data.portfolios : [];
  return portfolios.flatMap((p) => {
    const rows = Array.isArray(p?.rows) ? p.rows : [];
    return rows.map((row) => normalizeFairValueRow(row, p));
  });
};

const emptyFairValue = () => ({ fair_value: null, difference: null, sources: [], reports: [] });

// Each summary row gets `fairValue`: the sum over the requested portfolios, the difference
// to the row's market value, the sources and the reports for the tooltip. One portfolio
// without a fair value makes the security's fair value null.
export const mergeFairValues = (summaryRows, fairRows, portfolioIds) => {
  const requested = new Set((Array.isArray(portfolioIds) ? portfolioIds : portfolioIds == null ? [] : [portfolioIds]).map(String));
  const byKey = {};
  (fairRows || []).forEach((row) => {
    if (requested.size > 0 && !requested.has(String(row.portfolio))) {
      return;
    }
    (byKey[row.key] = byKey[row.key] || []).push(row);
  });

  return (summaryRows || []).map((row) => {
    const matches = byKey[fairValueKey(row?.related_model_type, row?.related_model?.id)] || [];
    if (matches.length === 0) {
      return { ...row, fairValue: emptyFairValue() };
    }
    const missing = matches.some((m) => m.fair_value === null);
    const fairValue = missing ? null : matches.reduce((sum, m) => sum + toCents(m.fair_value), 0) / 100;
    const marketValue = toNumber(row?.market_value);
    const difference =
      fairValue === null || marketValue === null ? null : (toCents(fairValue) - toCents(marketValue)) / 100;
    const sources = [];
    matches.forEach((m) => {
      if (m.source && !sources.includes(m.source)) {
        sources.push(m.source);
      }
    });
    const reports = matches
      .filter((m) => m.source === "report" && m.report)
      .map((m) => ({ portfolio: m.portfolio, portfolio_name: m.portfolio_name, ...m.report }));
    return { ...row, fairValue: { fair_value: fairValue, difference, sources, reports } };
  });
};

// Sum of the rows that have a fair value (two decimals) and how many have none.
export const fairValueTotals = (mergedRows) => {
  let cents = 0;
  let missing = 0;
  (mergedRows || []).forEach((row) => {
    const value = row?.fairValue?.fair_value;
    if (value === null || value === undefined) {
      missing += 1;
    } else {
      cents += toCents(value);
    }
  });
  return { total: cents / 100, missing };
};

const SOURCE_LABELS = {
  report: (t) => t("fair_value_source_report", "Raportti"),
  market_price: (t) => t("fair_value_source_market_price", "Markkinahinta"),
  acquisition_cost: (t) => t("fair_value_source_acquisition_cost", "Hankintameno"),
};

// One source or a list of them; different ones are joined with ", ".
export const sourceLabel = (source, t) => {
  const list = (Array.isArray(source) ? source : [source]).filter((s) => SOURCE_LABELS[s]);
  const labels = [];
  list.forEach((s) => {
    const label = SOURCE_LABELS[s](t);
    if (!labels.includes(label)) {
      labels.push(label);
    }
  });
  return labels.length > 0 ? labels.join(", ") : EMPTY_CELL;
};

// One tooltip line per portfolio's report: name, period, value date and received date.
export const reportTooltipLines = (reports) =>
  (reports || []).map((r) => ({
    portfolio_name: r.portfolio_name || "",
    report_period: r.report_period || "",
    value_date: formatDay(r.value_date),
    report_received: formatDay(r.report_received),
  }));

// Money cell: formatted amount, or "–" when there is none.
export const amountCell = (value) => {
  const text = value === null || value === undefined ? "" : formatAmount(value);
  return text ? { content: text, className: "text-right" } : EMPTY_CELL;
};

// The three fair value columns go right after the market value column, found by key.
export const fairValueHeaders = (headers, t) => {
  const added = [
    { label: t("fair_value_column", "Käypä arvo"), key: "fair_value" },
    { label: t("fair_value_difference_column", "Ero markkina-arvoon"), key: "fair_value_difference" },
    { label: t("fair_value_source_column", "Lähde"), key: "fair_value_source" },
  ];
  const out = [...(headers || [])];
  const index = out.findIndex((h) => h?.key === "market_value");
  if (index < 0) {
    return [...out, ...added];
  }
  out.splice(index + 1, 0, ...added);
  return out;
};
